import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageLoader, PageHeader } from "@/components/shared";
import { Disclaimer } from "@/components/Disclaimer";
import { ExternalLink, GraduationCap, ShieldAlert, Waves, FileText, Landmark, Search, Plug, Sparkles, Link as LinkIcon, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  Schools: GraduationCap, "Public Safety": ShieldAlert, "Flood / Hazard": Waves,
  "Tax / Public Records": FileText, Lender: Landmark, Inspection: Search, Utilities: Plug,
};

function ResourceCard({ r, i }) {
  const Icon = ICONS[r.category] || LinkIcon;
  return (
    <motion.a href={r.url} target="_blank" rel="noreferrer"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
      className="hg-card hg-card-hover p-5 block group" data-testid={`resource-${i}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-forest/10 text-forest flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2"><p className="font-semibold truncate">{r.title}</p><ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" /></div>
          <Badge variant="outline" className="mt-1 text-xs">{r.category}</Badge>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.description}</p>
        </div>
      </div>
    </motion.a>
  );
}

export default function Resources() {
  const resQ = useQuery({ queryKey: ["resources"], queryFn: async () => (await api.get("/buyer/resources")).data });
  const guideQ = useQuery({ queryKey: ["guidance"], queryFn: async () => (await api.get("/buyer/guidance")).data });
  const docsQ = useQuery({ queryKey: ["buyer-docs"], queryFn: async () => (await api.get("/buyer/documents")).data });
  if (resQ.isLoading) return <PageLoader />;

  const { default: def, custom } = resQ.data;
  const guidance = guideQ.data || [];
  const docs = docsQ.data || [];

  return (
    <div>
      <PageHeader title="Resource center" subtitle="Neutral, official third-party links so you can review public information yourself. We link — we don't interpret or rate." />

      {docs.length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Documents from your agent</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {docs.map((d) => (
              <a key={d.id} href={d.kind === "file" ? `${BACKEND}/api/documents/${d.id}/download` : d.url}
                target={d.kind === "file" ? undefined : "_blank"} rel="noreferrer"
                className="hg-card hg-card-hover p-4 flex items-center gap-3" data-testid={`buyer-doc-${d.id}`}>
                <div className="h-9 w-9 rounded-xl bg-forest/10 text-forest flex items-center justify-center shrink-0">
                  {d.kind === "file" ? <Download className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                </div>
                <div className="min-w-0"><p className="font-medium truncate">{d.title}</p>
                  <p className="text-xs text-muted-foreground">{d.kind === "file" ? "Download" : "Open link"}</p></div>
              </a>
            ))}
          </div>
        </div>
      )}

      {guidance.length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> From your agent</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {guidance.map((g, i) => (
              <Card key={g.id} className="p-5 rounded-2xl bg-primary/5 border-primary/20" data-testid={`guidance-${i}`}>
                <p className="font-semibold">{g.title}</p>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{g.body}</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {custom?.length > 0 && (
        <div className="mb-8">
          <h3 className="font-heading font-semibold text-lg mb-3">Recommended by your agent</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {custom.map((r, i) => <ResourceCard key={r.id} r={r} i={i} />)}
          </div>
        </div>
      )}

      <h3 className="font-heading font-semibold text-lg mb-3">Official & third-party resources</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {def.map((r, i) => <ResourceCard key={i} r={r} i={i} />)}
      </div>

      <div className="mt-8">
        <Disclaimer>These links go to independent, official, or third-party sources. Look Before You Leap 808 does not score schools, interpret crime data, or rate neighborhoods or safety. Review the information yourself and consult professionals.</Disclaimer>
      </div>
    </div>
  );
}
